import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { PartnerResolveResponse, resolvePartner, getCachedPartnerConfig } from "@/services/partnerResolution";
import { CircularProgress, Box, Typography } from "@mui/material";

/**
 * Partner context value
 */
interface PartnerContextValue {
  partner: PartnerResolveResponse | null;
  isLoading: boolean;
  error: Error | null;
  refreshPartner: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextValue | null>(null);

/**
 * Hook to access partner context
 */
export const usePartner = (): PartnerContextValue => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error("usePartner must be used within PartnerProvider");
  }
  return context;
};

interface PartnerProviderProps {
  children: ReactNode;
  email?: string;
  onPartnerResolved?: (partner: PartnerResolveResponse) => void;
}

/**
 * Partner Provider that resolves partner configuration before authentication
 */
export const PartnerProvider: React.FC<PartnerProviderProps> = ({ children, email, onPartnerResolved }) => {
  const [partner, setPartner] = useState<PartnerResolveResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const resolvePartnerConfig = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check for cached config first
      const cached = getCachedPartnerConfig();
      if (cached) {
        console.log("[PartnerProvider] Using cached partner config:", cached);
        setPartner(cached);
        onPartnerResolved?.(cached);
        setIsLoading(false);
        return;
      }

      // Resolve partner from backend
      const partnerConfig = await resolvePartner({ email });
      setPartner(partnerConfig);
      onPartnerResolved?.(partnerConfig);
    } catch (err) {
      console.error("[PartnerProvider] Failed to resolve partner:", err);
      setError(err instanceof Error ? err : new Error("Partner resolution failed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void resolvePartnerConfig();
  }, [email]);

  const refreshPartner = async () => {
    // Clear cache and re-resolve
    try {
      sessionStorage.removeItem("partner_config");
      await resolvePartnerConfig();
    } catch (err) {
      console.error("[PartnerProvider] Failed to refresh partner:", err);
      throw err;
    }
  };

  const value: PartnerContextValue = {
    partner,
    isLoading,
    error,
    refreshPartner,
  };

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
};

/**
 * Loading screen for partner resolution
 */
export const PartnerLoadingScreen: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    bgcolor="#f5f5f5"
  >
    <CircularProgress size={48} />
    <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
      Loading configuration...
    </Typography>
  </Box>
);

/**
 * Error screen for partner resolution failures
 */
export const PartnerErrorScreen: React.FC<{ error: Error; onRetry?: () => void }> = ({ error, onRetry }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    bgcolor="#f5f5f5"
    p={3}
  >
    <Typography variant="h5" gutterBottom color="error">
      Configuration Error
    </Typography>
    <Typography variant="body1" sx={{ mb: 3, textAlign: "center", maxWidth: 500 }}>
      {error.message || "Failed to load configuration. Please try again."}
    </Typography>
    {onRetry && (
      <button onClick={onRetry} style={{ padding: "8px 16px", cursor: "pointer" }}>
        Retry
      </button>
    )}
  </Box>
);
